import { useQuery } from '@tanstack/react-query';

import { listDietaryTags, listServiceTypes } from '../menus/api';

/** `code -> name` map, e.g. `{ SIN_TACC: 'Sin TACC' }`. */
export type CatalogNameMap = Record<string, string>;

function toNameMap(items: readonly { code: string; name: string }[] | undefined): CatalogNameMap {
  if (items === undefined) {
    return {};
  }

  return Object.fromEntries(items.map((item) => [item.code, item.name]));
}

/**
 * Same `queryKey: ['service-types']` every other screen already uses
 * (`menu-list-page.tsx`, `menu-detail-page.tsx`, `categories-page.tsx`,
 * `calendar-list-page.tsx`) — TanStack Query dedups the request across
 * screens instead of issuing a second fetch just for this one.
 */
export function useServiceTypeNames(): CatalogNameMap {
  const query = useQuery({ queryKey: ['service-types'], queryFn: listServiceTypes });

  return toNameMap(query.data);
}

/** Same `queryKey: ['dietary-tags']` convention as `useServiceTypeNames`. */
export function useDietaryTagNames(): CatalogNameMap {
  const query = useQuery({ queryKey: ['dietary-tags'], queryFn: listDietaryTags });

  return toNameMap(query.data);
}

/** Falls back to the raw code when the catalog has not loaded yet, or the code is unknown. */
export function resolveCatalogName(names: CatalogNameMap, code: string): string {
  return names[code] ?? code;
}
